import { create } from "zustand";
import { persist } from "zustand/middleware";

interface NavigationSidebarOpenStore {
  // Sidebar collapsed state (persisted)
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  // Hover overlay open state (not persisted)
  hoverOpen: boolean;
  setHoverOpen: (hoverOpen: boolean) => void;
  // Expanded nav sections (keyed by item name or route)
  expandedSections: string[];
  toggleSection: (sectionId: string) => void;
  isSectionExpanded: (sectionId: string) => boolean;
  expandSectionsForRoute: (sectionNames: string[]) => void;
}

export const useNavigationSidebarOpenStore =
  create<NavigationSidebarOpenStore>()(
    persist(
      (set, get) => ({
        isCollapsed: false,
        setIsCollapsed: (isCollapsed) => set({ isCollapsed }),
        hoverOpen: false,
        setHoverOpen: (hoverOpen) => set({ hoverOpen }),
        // Default: all sections collapsed (empty = none expanded)
        expandedSections: [],
        toggleSection: (sectionId) =>
          set((state) => {
            const isExpanded = state.expandedSections.includes(sectionId);
            return {
              expandedSections: isExpanded
                ? state.expandedSections.filter((id) => id !== sectionId)
                : [...state.expandedSections, sectionId],
            };
          }),
        isSectionExpanded: (sectionId) => {
          // Default to collapsed - only expanded if explicitly in the list
          return get().expandedSections.includes(sectionId);
        },
        expandSectionsForRoute: (sectionNames) =>
          set((state) => {
            // Add all section names that aren't already expanded
            const newSections = sectionNames.filter(
              (name) => !state.expandedSections.includes(name),
            );
            if (newSections.length === 0) return state;
            return {
              expandedSections: [...state.expandedSections, ...newSections],
            };
          }),
      }),
      {
        name: "navigation-sidebar-open-store",
        partialize: (state) => ({
          isCollapsed: state.isCollapsed,
          expandedSections: state.expandedSections,
        }),
      },
    ),
  );
